/**
 * space_look — the PURE rules of the sector's picture (2026-09-25 realism wave, research report §9 / §6 R6).
 *
 * Everything the space renderer decides by NUMBER lives here, three-free, so `verify:space-look` pins the
 * rule rather than a picture of it: the vacuum post profile, the space exposure clamp, the eclipse fraction,
 * the per-system sky rotation, the sun's disc laws (limb darkening, corona, temperature colour), the motion
 * cues (dust wrap, streaks, FOV, trauma), the plume and shock formulas, and the CPU mirror of the war hulls'
 * window layer. The TSL in lib/space/*.ts and the SpaceModule wiring read these; a shader that disagrees
 * with its mirror is the bug.
 *
 * NO-CYCLE RULE: numbers in, numbers out. Nothing here imports three, a module or the bus.
 */

export type V3 = { x: number; y: number; z: number };

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

// ── R1: the space post profile ──────────────────────────────────────────────────────────────────────────
/**
 * Passes a VACUUM has no business running. Aerial perspective, the analytic height fog and god rays are
 * atmosphere (the fog sat at its densest after every 25 km rebase — the camera's local y is ~0 — a grey band
 * in the last ground fog colour); clouds are gated already; GTAO / SSGI / SSR / contact shadows darken and
 * reflect against a black void for nothing; depth of field blurred every star and planet past 73 m on
 * high/ultra (its focus RANGE is metres of gameplay distance, not a lens).
 */
export const SPACE_DROPPED_PASSES = [
  'aerial', 'volumetricFog', 'godRays', 'clouds', 'ao', 'ssgi', 'ssr', 'contactShadows', 'capsuleShadows', 'dof', 'chromaticAberration',
] as const;
/**
 * Bloom in space: only true EMITTERS cross it (the sun disc at 40, plumes 4–12, flashes 30+, the top ~1 % of stars,
 * a glint). The threshold sits ABOVE the brightest lit diffuse surface — the key is 3.2, so a snow-white planet
 * reads ~2.9 in scene units: at the research's 1.0 the whole lit Commons bloomed into a grey haze over the limb
 * (the first launch picture, 2026-09-25).
 */
export const SPACE_KEY_INTENSITY = 3.2;
/**
 * THE ADDITIVE LAW (2026-09-25 session 2): the sector's glow is THRESHOLDED and ADDED — `composite: 'add'`, a few
 * down/up pairs. The tiers' bloom became the lighting wave's energy-conserving LERP (threshold-less, `passes` 5,
 * output = mix(scene, blur, strength)); inheriting that with the space strength 0.6 blurred 60 % of every frame —
 * the "pale veil over two thirds of the sun shot" and the "grey fog disc" of the shock ring were that blur, not
 * the sun or the ring. `passes` 3 (to ⅛ resolution): the additive path keeps only the SMALLEST level, so the
 * lerp's 5 pairs would put every emitter's glow at ¹⁄₃₂ resolution — a flat halo with no core.
 */
export const SPACE_BLOOM = { threshold: 3.4, strength: 0.6, radiusMin: 0.6, composite: 'add', passes: 3 } as const;
/** Lens flares fire only on true HDR emitters (the sun at 20–60, a capital's death flash) — never a lit hull. */
export const SPACE_FLARE_THRESHOLD = 4;

/**
 * The tier's post config → the sector's. Returns a NEW object (the tier preset is never mutated) and the
 * passes it dropped (telemetry). Bloom and flares keep the TIER's decision to exist (a tier without bloom
 * stays without it — WebGL2 strips bloom for the MRT reason anyway); only their thresholds move.
 */
export function spacePostConfig<T extends object>(post: T): { post: T; dropped: string[] } {
  const out = { ...post } as Record<string, unknown>;
  const dropped: string[] = [];
  for (const k of SPACE_DROPPED_PASSES) if (out[k] !== undefined) { delete out[k]; dropped.push(k); }
  const bloom = out.bloom as { threshold?: number; strength?: number; radius?: number } | undefined;
  if (bloom) {
    out.bloom = {
      ...bloom,
      threshold: SPACE_BLOOM.threshold,
      strength: Math.max(SPACE_BLOOM.strength, bloom.strength ?? 0),
      radius: Math.max(SPACE_BLOOM.radiusMin, bloom.radius ?? 0),
      composite: SPACE_BLOOM.composite,
      passes: SPACE_BLOOM.passes,
    };
  }
  const flares = out.lensFlares as { enabled?: boolean; brightThreshold?: number } | undefined;
  if (flares && flares.enabled !== false) out.lensFlares = { ...flares, brightThreshold: SPACE_FLARE_THRESHOLD };
  // THE FAR-PLANE NaN (session 3, 2026-09-27): the engine motion blur's camera/object SPLIT (a weight ≠ 1 — high / ultra
  // run camera 0.5) rebuilds each pixel's camera velocity from depth through the inverse projection; at a SKY pixel
  // (depth 1) under the sector's planes (near 0.05 m, far 3.6e6 m) the float32 inverse's w is exactly 0 (spaceFarPlaneW),
  // the velocity NaN, and the blur's odd taps sampled texel (0,0): the sky around every moving hull filled with the
  // frame's top-left colour (measured on WebGPU at high). Full weights = no split — the blur keeps its 180° shutter.
  // DROPPED (sky/post P2, 2026-09-27): the engine's `vel()` now splits only where `geometryMask(depth)` holds and both
  // frames' w are non-degenerate, and takes three's own velocity at sky pixels (the coordinator's 03:40 guard,
  // `motion-blur-shutter-split.test.ts`) — the sector keeps each tier's camera / object weights again (re-probed:
  // the `nan` leg at high, impl-p2-sky-post-space CHECKPOINT 4).
  return { post: out as T, dropped };
}

/**
 * Eye adaptation in space. The frame is mostly black, so a symmetric ±2 EV clamp would meter the void,
 * push to +2 EV and lift the Milky Way into a grey veil — the failure the profile exists to prevent.
 * The clamp is ASYMMETRIC: down to −2 EV (staring at the sun or a lit planet filling the view darkens the
 * frame) and at most +¼ EV up. Histogram metering over the 50–98 % band trims the sun's own pixels.
 */
export const SPACE_EXPOSURE = {
  key: 0.18,
  minExposure: 0.25,   // −2 EV
  maxExposure: 1.19,   // +¼ EV
  metering: 'histogram' as const,
  lowPercentile: 0.5,
  highPercentile: 0.98,
  speedUp: 1.4,
  speedDown: 0.7,
  intervalFrames: 8,
};
/**
 * The w the float32 inverse projection gives a far-plane (depth 1, WebGPU [0, 1] depth) point — what a depth-reconstructing
 * post pass divides by. `Math.fround` mirrors the GPU's float32 matrix: at the sector's planes (0.05 m / 3.6e6 m)
 * c = −f/(f−n) rounds to exactly −1 and w is 0 (an infinite view position); at the ground's (0.05 m / 120 km) it is not.
 */
export function spaceFarPlaneW(near: number, far: number): number {
  const f = Math.fround;
  const c = f(-far / (far - near)), d = f(-far * near / (far - near));
  return f(f(1 / d) + f(c / d));
}
/**
 * THE REGIME BASE (session 3, 2026-09-27). The sector's eye multiplies the ground curve's REFERENCE exposure × the
 * player's compensation (the player's own exposure in 'fixed' mode) — never the live ground exposure: its METERED
 * correction (−0.3 … +1.5 EV since the lighting wave made the ground meter honest) rode into the whole flight, so a launch
 * from a dim pad lifted the void by up to 1.5 stops ON TOP of the asymmetric clamp below. Every space constant (the key
 * 3.2, the bloom threshold 3.4, the sun disc 16, the star stretch) was tuned at base 0.85 = EXPOSURE_REF with no
 * correction (the session 1–2 sun legs: exposure ÷ (bias × adaptation) = 0.850 in all six runs).
 */
export const SPACE_BASE_FALLBACK = 0.85;
export function spaceBaseExposure(s: { mode: 'physical' | 'fixed'; exposure: number; exposureRef: number; compensation: number }): number {
  const b = s.mode === 'fixed' ? s.exposure : s.exposureRef * s.compensation;
  return Number.isFinite(b) && b > 0 ? b : SPACE_BASE_FALLBACK;
}
/** The clamp in stops (log2 of the exposure multiplier): [−2, +0.25]. */
export function spaceExposureStops(): [number, number] {
  return [Math.log2(SPACE_EXPOSURE.minExposure), Math.log2(SPACE_EXPOSURE.maxExposure)];
}

/**
 * Glare bias: the metering regions are small and centre-weighted, so a sun near the middle of the view can
 * sit between them. The pupil closes anyway — the base exposure is biased DOWN by up to 35 % when a visible
 * sun is near the centre, fading to nothing at the edges and off-screen, and by the ECLIPSE fraction (a sun
 * behind a planet closes nothing). Returns the multiplier on the base exposure, in [0.65, 1].
 */
export function sunGlareBias(visible: number, ndcDist: number, onScreen: boolean): number {
  if (!onScreen) return 1;
  const w = 1 - smoothstep(0.25, 1.1, ndcDist);
  return clamp(1 - 0.35 * clamp(visible, 0, 1) * w, 0.65, 1);
}

// ── R3: the sun ─────────────────────────────────────────────────────────────────────────────────────────
/** Area of the intersection of two discs (radii r1, r2, centre distance d) — the lens formula. */
export function discOverlapArea(r1: number, r2: number, d: number): number {
  if (r1 <= 0 || r2 <= 0) return 0;
  if (d >= r1 + r2) return 0;
  if (d <= Math.abs(r1 - r2)) { const r = Math.min(r1, r2); return Math.PI * r * r; }
  const a = clamp((d * d + r1 * r1 - r2 * r2) / (2 * d * r1), -1, 1);
  const b = clamp((d * d + r2 * r2 - r1 * r1) / (2 * d * r2), -1, 1);
  const k = (-d + r1 + r2) * (d + r1 - r2) * (d - r1 + r2) * (d + r1 + r2);
  return r1 * r1 * Math.acos(a) + r2 * r2 * Math.acos(b) - 0.5 * Math.sqrt(Math.max(0, k));
}

/**
 * The visible fraction of the sun's disc: every occluder (a planet, a capital's bounding sphere) seen from
 * the camera as a disc of angular radius `angR` in direction `dir` covers part of the sun disc (angular
 * radius `sunAngR` along `sunDir`). Independent occluders compose as a product of their clear fractions
 * (two moons over one sun never read as "more than eclipsed"). 1 = in full view, 0 = total eclipse.
 * Directions are unit vectors from the camera; angles in radians (small-angle planar overlap — the sun is
 * a degree across, planets tens of degrees at worst, and the overlap geometry is the tangent-plane one).
 */
export function eclipseVisible(sunDir: V3, sunAngR: number, occluders: ReadonlyArray<{ dir: V3; angR: number }>): number {
  const sunArea = Math.PI * sunAngR * sunAngR;
  if (sunArea <= 0) return 1;
  let vis = 1;
  for (const o of occluders) {
    const c = clamp(sunDir.x * o.dir.x + sunDir.y * o.dir.y + sunDir.z * o.dir.z, -1, 1);
    if (c <= 0) continue; // behind the camera relative to the sun
    const sep = Math.acos(c);
    if (sep >= sunAngR + o.angR) continue;
    vis *= 1 - clamp(discOverlapArea(sunAngR, o.angR, sep) / sunArea, 0, 1);
  }
  return clamp(vis, 0, 1);
}
/** A body's angular radius from a distance (asin, clamped inside the body). */
export function angularRadius(radius: number, dist: number): number {
  return dist <= radius ? Math.PI / 2 : Math.asin(radius / dist);
}

/** The quadratic limb-darkening law (solar fit): I(μ) = 0.30 + 0.94μ − 0.24μ², μ = √(1 − (r/R)²). I(1) = 1. */
export function limbDarkening(rOverR: number): number {
  const mu = Math.sqrt(Math.max(0, 1 - rOverR * rOverR));
  return 0.30 + 0.94 * mu - 0.24 * mu * mu;
}
/** The corona's radial falloff: (r/R)^−2.5 outside the disc (1 at the limb), 0 inside (the disc covers it). */
export function coronaFalloff(rOverR: number): number {
  return rOverR < 1 ? 0 : Math.pow(rOverR, -2.5);
}
/**
 * THE CORONA FOLLOWS THE EYE (2026-09-27, sky/post P3 — realism-additions §7.2): the corona is a fixed radiance falling
 * as (r/R)^−2.5, so how FAR out it reads is where that radiance × the eye's exposure meets the display's floor — a
 * dark-adapted eye (the sun just entering the frame) sees a long corona, an eye stopped down on the disc a short one.
 * The quad's extent was a fixed 9 radii at every exposure (the corona cut off at a hard ring the eye never saw move).
 * Now: the extent where the falloff drops to CORONA_FLOOR of its limb value at the REGIME BASE, scaled by
 * (exposure / base)^(1/2.5) — the radius at which the same displayed radiance is reached — clamped. At the base it is
 * the old 9 radii (the look the space agent tuned). The glare's spikes (a lens effect) do not change.
 */
/**
 * THE WIDE-QUAD LAW (2026-10-08, found by the space-kit probe in a charted world's own system). A star's three quads
 * were seated PARALLEL TO THE IMAGE PLANE at the star's direction. A quad's coordinate is then the tangent of the
 * angle off the VIEW AXIS, shifted — not of the angle off the STAR — and its plane stands D·cos θs in front of the eye,
 * so a quad sized for 69° (every star wider than ~1.6° has its glare and corona at that cap) covers the WHOLE frame
 * once the star is 60–90° off the axis: with a 14° star 79° to the side, the frame's centre read "3.9 disc radii from
 * the star" and took the glare's halo (0.17 of the star's colour before exposure — a grey veil over everything, which
 * popped off as the star passed 90°), where the rig's own profile ends 68.75° from the star. A quad that FACES THE EYE
 * (its normal along the star's direction) reads tan(angle off the star) exactly — THE TANGENT LAW's own premise.
 * The seat turns from the image plane's to the star's as the glare's half-angle goes `WIDE_QUAD_FROM_RAD` →
 * `WIDE_QUAD_FULL_RAD`: the Commons Reach's sun (0.55°: a glare of 0.40 rad) keeps the seat it always had, bit for bit.
 */
export const WIDE_QUAD_FROM_RAD = 0.45;
export const WIDE_QUAD_FULL_RAD = 0.8;
/**
 * THE FAR-PLANE ROOM. A quad that faces the eye reaches D ÷ cos(angle off the star) away: the corner of a 69° quad is
 * 3.8 D out, and at the rig's 2,500 km that is past the sector's 3,600 km far plane — the glow would be cut where it is
 * still alight. A wide star's quads stand nearer, their farthest corner `WIDE_QUAD_REACH_M` from the eye (875 km for the
 * widest). Nothing reads a quad's distance but the depth test against hulls and bodies nearer than that — the shaders
 * work in disc radii. `ext` = the widest quad's half-size ÷ its distance (the tangent of its half-angle).
 */
export const WIDE_QUAD_REACH_M = 3_300_000;
export function wideQuadDistance(d: number, ext: number, wide: number): number {
  if (!(wide > 0) || !Number.isFinite(ext)) return d;
  return Math.min(d, WIDE_QUAD_REACH_M / Math.sqrt(1 + 2 * ext * ext));
}
export function wideQuadWeight(glareHalfRad: number): number {
  if (!Number.isFinite(glareHalfRad)) return 0;
  const t = Math.max(0, Math.min(1, (glareHalfRad - WIDE_QUAD_FROM_RAD) / (WIDE_QUAD_FULL_RAD - WIDE_QUAD_FROM_RAD)));
  return t * t * (3 - 2 * t);
}
/**
 * THE WIDE STAR'S EDGE (2026-10-08; `space_sun.ts wideEdge` is the TSL mirror). A star's corona and glare end with a
 * smoothstep over the last 40 % / 30 % of their extent — measured in TANGENT units, where the last third of the way
 * to the 69° cap is eight degrees of sky. That is invisible on the Commons Reach's sun (its glare is 0.007 of the
 * star's colour where the fade begins) and a hard ARC on a wide star, whose halo — written in disc radii — is still
 * 0.05 there: once THE WIDE-QUAD LAW took the veil away, the glow of a star to the side ended in a visible curve
 * 61–69° from it. A quad of a wide star that THE CAP CUT SHORT fades over the last THREE TENTHS OF ITS ANGLE instead
 * (48° → 69°), by 1 − smoothstep: the profile the edge always had, measured by angle and begun earlier. Inside seven
 * tenths of the angle nothing changes: a star in the middle of the frame keeps the glow it had out to the frame's edge
 * (and 98.5 % of it in the far corners).
 * NOT SQUARED (the first cut was, for an hour): the sector's display is AgX — a sigmoid over LOG luminance, not a power
 * law — so a second-order zero in linear light buys nothing at the end (the toe is flat there already) and front-loads
 * the drop. Measured on the kit's pictures and reproduced by `verify:space-look` §3c to 2 levels: the pictured glow fell
 * 4.0 levels a degree mid-fade squared against 3.0 plain (its own body falls 1.6), and a corona that ended INSIDE the
 * frame lost half its light through its whole fade — the star-centred picture's edges at 0.65–0.75 of the old glow.
 * THE WEIGHTS. The glare's is the wide-quad weight (0 for the Commons Reach's sun: the old edge, the same numbers). The
 * corona's is that × `wideEdgeCapShare`: a corona's extent follows the eye — it ends where its own light meets the
 * display's floor — and such a corona keeps the edge it had, bit for bit; only one the 69° cap cut short (a dark-adapted
 * eye beside a wide star) is still alight at its edge and takes the fade by angle.
 */
export const WIDE_EDGE_FROM = 0.7;
export function wideEdgeProfile(psiRad: number, halfRad: number): number {
  if (!(halfRad > 0) || !Number.isFinite(psiRad)) return 0;
  const t = Math.max(0, Math.min(1, (psiRad / halfRad - WIDE_EDGE_FROM) / (1 - WIDE_EDGE_FROM)));
  return 1 - t * t * (3 - 2 * t);
}
/** The share of THE WIDE STAR'S EDGE a quad of half-angle `halfRad` takes under the rig's cap `capRad`: 0 while it ends inside `WIDE_EDGE_CAP_FROM` of the cap (it ends where its own light does), 1 at the cap (the cap cut it). */
export const WIDE_EDGE_CAP_FROM = 0.85;
export function wideEdgeCapShare(halfRad: number, capRad: number): number {
  if (!(capRad > 0) || !Number.isFinite(halfRad)) return 0;
  const t = Math.max(0, Math.min(1, (halfRad / capRad - WIDE_EDGE_CAP_FROM) / (1 - WIDE_EDGE_CAP_FROM)));
  return t * t * (3 - 2 * t);
}
/**
 * The orientation of a star's quads: the camera's (`c*`, a unit quaternion) carried a share `w` of the way along the
 * shortest turn that takes the view axis (the camera's −Z) onto the unit direction `d*` to the star. w = 0 is the
 * camera's own orientation, the same numbers; w = 1 seats the quad's normal on −d (it faces the eye) and keeps its
 * right / up the camera's, parallel-transported. Written into `out` [x, y, z, w]; plain arithmetic, no three.
 */
export function wideQuadQuat(cx: number, cy: number, cz: number, cw: number, dx: number, dy: number, dz: number, w: number, out: number[]): number[] {
  out[0] = cx; out[1] = cy; out[2] = cz; out[3] = cw;
  if (!(w > 0) || !Number.isFinite(dx + dy + dz)) return out;
  // the view axis: (0, 0, −1) turned by the camera
  const fx = -2 * (cx * cz + cw * cy), fy = -2 * (cy * cz - cw * cx), fz = -(1 - 2 * (cx * cx + cy * cy));
  let ax = fy * dz - fz * dy, ay = fz * dx - fx * dz, az = fx * dy - fy * dx;
  const sin = Math.hypot(ax, ay, az), cos = fx * dx + fy * dy + fz * dz;
  if (sin < 1e-9) {
    if (cos > 0) return out;                                   // the star is dead ahead: the two seats are one
    // dead astern: any axis across the view will do (nothing of the quad is in the picture)
    if (Math.abs(fx) > Math.abs(fz)) { ax = -fy; ay = fx; az = 0; } else { ax = 0; ay = -fz; az = fy; }
    const n = Math.hypot(ax, ay, az) || 1; ax /= n; ay /= n; az /= n;
  } else { ax /= sin; ay /= sin; az /= sin; }
  const half = 0.5 * Math.atan2(sin, cos) * Math.min(1, w), s = Math.sin(half), qw = Math.cos(half);
  const qx = ax * s, qy = ay * s, qz = az * s;
  // (the turn) × (the camera): the camera's frame first, then the turn toward the star
  out[0] = qw * cx + qx * cw + qy * cz - qz * cy;
  out[1] = qw * cy - qx * cz + qy * cw + qz * cx;
  out[2] = qw * cz + qx * cy - qy * cx + qz * cw;
  out[3] = qw * cw - qx * cx - qy * cy - qz * cz;
  return out;
}
/**
 * The oracle's slice (the plane of the view axis and the star): the coordinate — in tangent units from the quad's
 * centre, signed toward the axis's far side — that a view ray `phi` rad off the axis reads on a quad seated at a star
 * `thetaS` rad off the axis and turned `tilt` rad from the image plane toward the star. null = the ray never meets it.
 * tilt = thetaS gives tan(phi − thetaS); tilt = 0 gives cos θs·tan φ − sin θs (the veil).
 */
export function quadTangentOnSeat(thetaS: number, phi: number, tilt: number): number | null {
  const sn = Math.cos(thetaS - tilt), rn = Math.cos(phi - tilt);
  if (!(sn > 1e-9) || !(rn > 1e-9)) return null;
  const t = sn / rn;
  return t * Math.sin(phi - tilt) - Math.sin(thetaS - tilt);
}
export const CORONA_EXTENT_AT_BASE = 9;
export const CORONA_EXTENT_MIN = 4;
export const CORONA_EXTENT_MAX = 24;
export function coronaExtentRadii(exposure: number, base: number): number {
  if (!(exposure > 0) || !(base > 0)) return CORONA_EXTENT_AT_BASE;
  const r = CORONA_EXTENT_AT_BASE * Math.pow(exposure / base, 1 / 2.5);
  return Math.max(CORONA_EXTENT_MIN, Math.min(CORONA_EXTENT_MAX, r));
}
/** The sector's eye as the sun rig reads it (SpaceExposure writes it each tick; 0 = no eye yet → the base extent). */
export const spaceEyeState = { exposure: 0, base: 0 };
/**
 * A star's light colour from its temperature (Kelvin → linear-ish sRGB, Tanner Helland's blackbody fit,
 * normalised so the brightest channel is 1). 5800 K reads warm white, 3500 K orange, 10000 K blue-white.
 */
export function kelvinToRgb(kelvin: number): [number, number, number] {
  const t = clamp(kelvin, 1000, 40000) / 100;
  let r: number, g: number, b: number;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    b = 255;
  }
  const c = [clamp(r, 0, 255) / 255, clamp(g, 0, 255) / 255, clamp(b, 0, 255) / 255];
  const m = Math.max(c[0], c[1], c[2], 1e-6);
  return [c[0] / m, c[1] / m, c[2] / m];
}
/** A star's temperature from its hex colour's hue (the galaxy's STAR_COLORS carry no Kelvin): the red/blue balance → 3000–12000 K. */
export function kelvinOfHex(hex: number): number {
  const r = ((hex >> 16) & 255) / 255, b = (hex & 255) / 255;
  const bal = clamp((b - r + 1) / 2, 0, 1); // 0 = red, 0.5 = white, 1 = blue
  return 3000 + bal * bal * 9000 + bal * 1800;
}
/** Key-light intensity by inverse square from the star, FLOORED for play ("dimmer far out", never black). */
export function sunIntensityAt(distM: number, refDistM: number, base: number, floor = 0.45): number {
  if (distM <= refDistM) return base;
  return base * Math.max(floor, (refDistM / distM) ** 2);
}

/** The sector sun (sys0 has no star body — it sits at infinity along this direction). */
export const SYS0_SUN_DIR: V3 = (() => { const l = Math.hypot(0.55, 0.8, 0.3); return { x: 0.55 / l, y: 0.8 / l, z: 0.3 / l }; })();
/** sys0's sun disc angular radius (a game sun reads ~2× the real one's 0.27°). */
export const SYS0_SUN_ANG_R = (0.55 * Math.PI) / 180;
/** The sun disc's emission (HDR — far above the bloom and flare thresholds).
 *  WHY 16, not 40: at 40 the mip bloom's widest levels spread a pale veil over two thirds of the frame
 *  whenever the sun was in view (the sun leg's shot). 16 is still 4.7× the bloom threshold and 4× the
 *  flare threshold — the disc clips to white and keeps its streak — without washing out the sky. */
export const SUN_DISC_EMISSION = 16;

// ── R2: the starfield ───────────────────────────────────────────────────────────────────────────────────
/** FNV-1a 32-bit (the same constants as the war's warH). */
export function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
const h01 = (s: string): number => fnv1a(s) / 4294967296;
/**
 * Every system's sky is the same astrophoto under a different ORIENTATION — a uniform random rotation
 * (Shoemake's quaternion from three hashed uniforms) seeded by the system id, so a hyperwarp lands under a
 * different sky and the Earth constellations of the photo are never recognisable away from home. sys0 (the
 * Commons Reach) keeps the identity: it is the SAME sky its ground shows. Returns [x, y, z, w], unit length.
 */
export function systemSkyQuat(systemId: string): [number, number, number, number] {
  if (systemId === 'sys0') return [0, 0, 0, 1];
  const u1 = h01(`${systemId}:sky:a`), u2 = h01(`${systemId}:sky:b`), u3 = h01(`${systemId}:sky:c`);
  const s1 = Math.sqrt(1 - u1), s2 = Math.sqrt(u1);
  return [s1 * Math.sin(2 * Math.PI * u2), s1 * Math.cos(2 * Math.PI * u2), s2 * Math.sin(2 * Math.PI * u3), s2 * Math.cos(2 * Math.PI * u3)];
}
/** Rotate a vector by a unit quaternion (the CPU mirror of the background's matrix). */
export function rotateByQuat(q: readonly [number, number, number, number], v: V3): V3 {
  const [x, y, z, w] = q;
  const tx = 2 * (y * v.z - z * v.y), ty = 2 * (z * v.x - x * v.z), tz = 2 * (x * v.y - y * v.x);
  return { x: v.x + w * tx + (y * tz - z * ty), y: v.y + w * ty + (z * tx - x * tz), z: v.z + w * tz + (x * ty - y * tx) };
}
/** The star gain that lifts the catalogue's 99th-percentile PEAK radiance to `target` (so the top ~1 % bloom). */
export function starGainFor(p99PeakRadiance: number, target = 1.2): number {
  return p99PeakRadiance > 0 ? target / p99PeakRadiance : 1;
}
/** The q-quantile of a sample (sorted copy; small arrays only). */
export function quantile(values: ArrayLike<number>, q: number): number {
  const a = Array.from(values).filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
  if (!a.length) return 0;
  return a[clamp(Math.floor(q * (a.length - 1)), 0, a.length - 1)];
}
/**
 * The vacuum magnitude stretch (lib/space/space_stars.ts): radiance = TARGET · x^γ with x = peak / p99 — a
 * steep γ BELOW the 99th percentile (the faint majority goes to black: ~12 % of the catalogue stays above a
 * visible 0.02, a short-exposure sky) and a gentle one above it (the brightest dozen bloom without whiting out).
 */
export const SPACE_STAR_GAMMA = 2.8;
export const SPACE_STAR_GAMMA_HI = 1.2;
export const SPACE_STAR_P99_TARGET = 3.8; // just over SPACE_BLOOM.threshold: the top ~1 % bloom
export function spaceStarRadiance(peak: number, p99: number, gamma = SPACE_STAR_GAMMA, target = SPACE_STAR_P99_TARGET): number {
  if (p99 <= 0) return 0;
  const x = Math.max(0, peak) / p99;
  return target * Math.pow(x, x < 1 ? gamma : SPACE_STAR_GAMMA_HI);
}
/** Background (the starless Milky Way photo) gain in space: deep blacks, the band still legible. */
export const SPACE_BG_GAIN = 0.22;

// ── R7: motion cues ─────────────────────────────────────────────────────────────────────────────────────
export const DUST_BOX_M = 60;
export const DUST_SHUTTER_S = 1 / 30;
/** A mote's position in the box WRAPPED around the camera: fract((p − cam)/L)·L − L/2 ∈ [−L/2, L/2). */
export function wrapDust(p: number, cam: number, L = DUST_BOX_M): number {
  const f = (p - cam) / L;
  return (f - Math.floor(f)) * L - L / 2;
}
/** Streak length along the relative velocity (a 1/30 s shutter), capped at half the box. */
export function dustStretchM(speed: number, shutter = DUST_SHUTTER_S, L = DUST_BOX_M): number {
  return Math.min(L * 0.5, Math.max(0, speed) * shutter);
}
/** Motes fade IN with speed (a parked ship sees no dust) and OUT at hyper (the streaks take over). */
export function dustAlpha(speed: number): number {
  return smoothstep(6, 40, speed) * (1 - smoothstep(2500, 6000, speed));
}
/** A streak is never longer than this share of its mote's distance from the CAMERA: it subtends at most asin(0.4) ≈ 24°. */
export const DUST_TAIL_FRAC = 0.4;
/** The plane in front of the camera a streak is cut at (m in front of the eye). */
export const DUST_CLIP_M = 0.25;
/** A mote fades in between these distances from the CAMERA (m): one against the lens would be a bar across the frame. */
export const DUST_NEAR_FADE_M: readonly [number, number] = [0.6, 2.5];
export interface DustStreak { visible: boolean; head: [number, number, number]; tail: [number, number, number] }
/**
 * A mote's streak in VIEW space (the camera at the origin looking down −z; `head` = the mote now, `smear` = the
 * relative velocity × the shutter, so `head + smear` is where it sat a shutter ago). The tail is kept within
 * DUST_TAIL_FRAC of the mote's distance from the camera, and the segment is CUT at the plane DUST_CLIP_M in front of
 * the eye: an end behind that plane moves onto it; a streak wholly behind it is not drawn.
 * WHY: the two ends are projected separately. An end behind the camera has w ≤ 0 — the old vertex stage divided it by
 * a clamped w, and the rasteriser drew the streak as a line from its tail THROUGH the velocity's vanishing point to the
 * far edge of the frame (the lines across the screen, worse with speed; 2026-10-07). The old tail clamp also measured
 * the mote's distance from the BOX centre (the ship), not the camera. motion_dust.ts's vertex stage mirrors this.
 */
export function dustStreakView(head: readonly [number, number, number], smear: readonly [number, number, number]): DustStreak {
  const r = Math.hypot(head[0], head[1], head[2]), sl = Math.hypot(smear[0], smear[1], smear[2]);
  const k = Math.min(1, (DUST_TAIL_FRAC * r) / Math.max(sl, 1e-3));
  let h: [number, number, number] = [head[0], head[1], head[2]];
  let t: [number, number, number] = [head[0] + smear[0] * k, head[1] + smear[1] * k, head[2] + smear[2] * k];
  const zc = -DUST_CLIP_M, hb = h[2] >= zc, tb = t[2] >= zc;
  if (hb && tb) return { visible: false, head: h, tail: t };
  if (hb !== tb) {
    const dz = t[2] - h[2], u = Math.min(1, Math.max(0, (zc - h[2]) / (dz >= 0 ? dz + 1e-4 : dz - 1e-4)));
    const p: [number, number, number] = [h[0] + (t[0] - h[0]) * u, h[1] + (t[1] - h[1]) * u, h[2] + (t[2] - h[2]) * u];
    if (hb) h = p; else t = p;
  }
  return { visible: true, head: h, tail: t };
}
/** A streak's half width on screen (px) — a mote is a line of light, never a world-sized quad. */
export const DUST_HALF_WIDTH_PX = 1;
/**
 * The motes' radiance gain over their tint. FAINT: at 0.5 a mote's brightest pixel is 0.29 where the sky's star cores
 * reach 6.8, and the whole field adds 8–49 % to the sky's light. (1.4 was written when no proper streak had ever been
 * drawn: there the dust added MORE light than the whole sky at overcruise — measured in the dust bed, 2026-10-07.)
 */
export const DUST_GAIN = 0.5;
/** The box-edge fade, as shares of the box: full inside 0.32·L of its centre, gone at 0.5·L (hides the wrap). */
export const DUST_EDGE_FADE: readonly [number, number] = [0.32, 0.5];
/** fnv1a + murmur3's fmix32 finalizer, in [0, 1). */
function dustHash01(s: string): number {
  let h = fnv1a(s);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
/**
 * Mote `i`'s seat in [0, L)³ and its brightness in [0.4, 1).
 * WHY the finalizer: bare FNV-1a is arithmetic in its LAST character, and the three keys differ only there — x, y and z
 * came out within 0.47 m of each other, so all the motes sat on the box's diagonal: a string, not a field (2026-10-07).
 */
/**
 * A belt rock's shape, pose and spin from its key (THE HASH LAW, 2026-10-08). Bare FNV-1a over keys that differ in
 * their LAST character ('sx' | 'sy' | 'sz', 'a' | 'b' | 'c', 'u' | 'v' | 'w') lands the three values within 0.4–0.8 %
 * of each other: every rock kept the master's proportions (mean axis ratio 1.013 where 1.35 was drawn for), turned
 * through one family of poses (three Euler angles 2.8° apart) and spun about the same diagonal (the median axis within
 * 1° of (1,1,1)). The finalizer makes the nine independent.
 */
export function rockSeat(key: string, i: number): { scale: [number, number, number]; euler: [number, number, number]; axis: [number, number, number]; rate: number } {
  const k = `${key}:${i}`, h = (s: string): number => dustHash01(k + s);
  const ax = h('u') - 0.5, ay = h('v') - 0.5, az = h('w') - 0.5, l = Math.hypot(ax, ay, az) || 1;
  return {
    scale: [0.72 + h('sx') * 0.58, 0.72 + h('sy') * 0.58, 0.72 + h('sz') * 0.58],
    euler: [h('a') * 6.283, h('b') * 6.283, h('c') * 6.283],
    axis: [ax / l, ay / l, az / l],
    rate: 0.01 + h('r') * 0.05,
  };
}
export function dustSeat(i: number, L = DUST_BOX_M): [number, number, number, number] {
  return [dustHash01(`dust:${i}:x`) * L, dustHash01(`dust:${i}:y`) * L, dustHash01(`dust:${i}:z`) * L, 0.4 + dustHash01(`dust:${i}:b`) * 0.6];
}
/** Radial star-streak strength 0..1: overcruise (×10 = 1,050 m/s) starts it, hyper (30 km/s) saturates it. */
export function starStreak(speed: number): number {
  return smoothstep(900, 30000, speed);
}
/** FOV kick in degrees: +6 on boost, +12 in hyper (eased by the caller), never stacked past 12. */
export function fovKickDeg(boost01: number, hyper01: number): number {
  return Math.min(12, 6 * clamp(boost01, 0, 1) + 12 * clamp(hyper01, 0, 1));
}
/** Trauma → shake amplitude (trauma², the Squirrel Eiserloh law) and its decay per second. */
export function shakeAmp(trauma: number): number { const t = clamp(trauma, 0, 1); return t * t; }
export const TRAUMA_DECAY_PER_S = 1.6;
/** Trauma a blast adds at a distance: full inside one hull length, gone past ten. */
export function blastTrauma(distM: number, lenM: number): number {
  const L = Math.max(10, lenM);
  return 0.9 * (1 - smoothstep(L, L * 10, distM));
}

// ── R5: plumes and explosions ───────────────────────────────────────────────────────────────────────────
/** The planar shockwave's radius: r(t) = R(1 − e^{−t/τ}). */
export function shockRadius(t: number, R: number, tau: number): number {
  return t <= 0 ? 0 : R * (1 - Math.exp(-t / tau));
}
/**
 * The shock FRONT's profile across the annulus (rr = radius / R, the CPU mirror of space_fx's ring shader). Session 2:
 * a 0.86 band with a 0.9–0.985 rim and an inner glow still read as a grey fog band in the boom shot (a rim that
 * fills its band is a band). The front is a thin HOT edge: zero at the inner lip, peaking at 0.992, zero at the
 * rim — and it fades as the CUBE of its remaining life at a gain the bloom takes (never a grey lingering disc).
 */
export const SHOCK_RING = { inner: 0.93, peak: 0.992, gain: 14 } as const;
export function shockRimProfile(rr: number): number {
  const rise = smoothstep(SHOCK_RING.inner, SHOCK_RING.peak, rr);
  return rise * rise * (1 - smoothstep(SHOCK_RING.peak, 1, rr)); // the rise SQUARED: the light gathers at the front
}
export function shockFade(age01: number): number { const k = 1 - clamp(age01, 0, 1); return k * k * k; }
/** Mach diamonds: `pow(½ + ½cos(2πz/D), 8)`, one nozzle diameter apart, ONLY above 60 % throttle, decaying down the plume. */
export function machDiamond(zM: number, nozzleDiamM: number, throttle: number): number {
  if (throttle <= 0.6 || nozzleDiamM <= 0) return 0;
  const on = smoothstep(0.6, 0.8, throttle);
  const node = Math.pow(0.5 + 0.5 * Math.cos((2 * Math.PI * zM) / nozzleDiamM), 8);
  return on * node * Math.exp(-zM / (nozzleDiamM * 6));
}
/** Plume core emission: 4 idle-burn → 12 at full throttle, boost ×1.25, never above 12 (it blooms, it never whites the frame). */
export function plumeEmission(throttle: number, boost: boolean): number {
  return Math.min(12, (4 + 8 * clamp(throttle, 0, 1)) * (boost ? 1.25 : 1));
}
/** The plume's axial temperature ramp at z01 ∈ [0,1] (nozzle → tail): white-blue core → the civ hue → dim. Returns weights [core, civ, tail]. */
export function plumeRamp(z01: number): [number, number, number] {
  const z = clamp(z01, 0, 1);
  const core = 1 - smoothstep(0.0, 0.25, z);
  const tail = smoothstep(0.45, 1.0, z);
  const civ = Math.max(0, 1 - core - tail);
  return [core, civ, tail];
}
/** Plume length (m) from the nozzle radius, throttle and boost. */
export function plumeLengthM(nozzleR: number, throttle: number, boost: boolean): number {
  // 2 → 8 nozzle radii (1.4× on boost): the first cut at 3 → 12 reached the chase camera 15 m astern and filled the frame.
  return nozzleR * (2 + 6 * clamp(throttle, 0, 1)) * (boost ? 1.4 : 1);
}
/** How many nozzles a hull class carries (capitals 8, frigates 4, corvettes 3, fighters 2). */
export function nozzleCount(lenM: number): number {
  return lenM >= 1000 ? 8 : lenM >= 90 ? 4 : lenM >= 25 ? 3 : 2;
}
/**
 * Nozzle seats from a hull's local box (nose −Z, so the stern is +Z): a row across the stern face at 80 %
 * of the half-width, two rows for four or more, radius from the stern face's area shared among them.
 */
export function nozzleSeats(box: { minX: number; maxX: number; minY: number; maxY: number; maxZ: number }, n: number): Array<{ x: number; y: number; z: number; r: number }> {
  const w = box.maxX - box.minX, h = box.maxY - box.minY;
  const cx = (box.minX + box.maxX) / 2, cy = (box.minY + box.maxY) / 2;
  const rows = n >= 4 ? 2 : 1;
  const perRow = Math.ceil(n / rows);
  // A nozzle mouth is a fraction of the stern it sits on (0.55 of the packing radius — the first cut's full packing
  // radius read as fat tubes on the fighters).
  const r = Math.max(0.15, 0.55 * Math.min(w / (perRow * 2.6), h / (rows * 2.6)));
  const out: Array<{ x: number; y: number; z: number; r: number }> = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / perRow), col = i % perRow;
    const u = perRow === 1 ? 0 : (col / (perRow - 1)) * 2 - 1;
    const v = rows === 1 ? 0 : row === 0 ? -0.5 : 0.5;
    out.push({ x: cx + u * w * 0.4, y: cy + v * h * 0.4, z: box.maxZ, r });
  }
  return out;
}
/**
 * THE STERN LAW (session 2, the plume shot): nozzles sit on the STERN SECTION, never the whole hull's box. The
 * whole box of a winged hull is its SPAN — the first cut seated a fighter's two plumes at its wing tips and sized
 * them from the span (two fat tubes), and when the shield ellipsoid (×1.25) was already a child the box grew past
 * the hull. The section is the rearmost `STERN_SLICE` of the length, and its extent is a PERCENTILE box of the
 * vertices there (8 % … 92 %): a stray wing-tip vertex never widens it. Null when the slice holds too few samples
 * (the caller keeps the whole box).
 */
export const STERN_SLICE = 0.06;
export function sternExtent(xs: ArrayLike<number>, ys: ArrayLike<number>, lo = 0.08, hi = 0.92): { minX: number; maxX: number; minY: number; maxY: number } | null {
  const n = Math.min(xs.length, ys.length);
  if (n < 12) return null;
  const sx = Array.from({ length: n }, (_, i) => xs[i]).sort((a, b) => a - b);
  const sy = Array.from({ length: n }, (_, i) => ys[i]).sort((a, b) => a - b);
  const q = (arr: number[], f: number): number => arr[Math.min(n - 1, Math.max(0, Math.round(f * (n - 1))))];
  const box = { minX: q(sx, lo), maxX: q(sx, hi), minY: q(sy, lo), maxY: q(sy, hi) };
  return box.maxX - box.minX > 1e-6 && box.maxY - box.minY > 1e-6 ? box : null;
}
/**
 * A fireball's COOLING colour at t01 of its life (vacuum: no smoke, no billow): white-hot → orange → dark red → out.
 * Linear rgb and an HDR gain (hot = bright).
 */
export function coolingColor(t01: number): { r: number; g: number; b: number; gain: number } {
  const t = clamp(t01, 0, 1);
  const r = 1;
  const g = 1 - 0.55 * smoothstep(0, 0.35, t) - 0.35 * smoothstep(0.35, 0.8, t);
  const b = 1 - 0.85 * smoothstep(0, 0.2, t) - 0.15 * smoothstep(0.2, 0.5, t);
  const gain = 18 * Math.pow(1 - t, 2.2) + 0.5 * (1 - smoothstep(0.7, 1, t));
  return { r, g: clamp(g, 0, 1), b: clamp(b, 0, 1), gain };
}
/** The explosion flash's HDR intensity at t seconds (a 30+ spike that dies in ~0.25 s). */
export function flashIntensity(t: number): number {
  return t < 0 ? 0 : 36 * Math.exp(-t / 0.08);
}

// ── R5: the FX batch's budget — which spawn earns a slot when the batch is full ────────────────────────
/**
 * The batch's LIVE budget per tier (the GPU capacity is fixed; the budget is how many effects may be alive at
 * once). One capital death is ~150 entries (up to 14 secondaries × 12 + the core's ~47) and a forced 10 v 10
 * dropped 148 spawns at the old flat 768, the NEWEST first — so the budget grows with the tier, and a full
 * batch EVICTS by significance instead of refusing whatever arrives last.
 */
export function fxBudget(tier: string): number {
  return tier === 'high' || tier === 'ultra' ? 1536 : tier === 'minimal' || tier === 'potato' ? 768 : 1024;
}
/** Kind weights: the flash and the fireballs carry a death; embers, shrapnel and sparks are its texture. */
export const FX_KIND_WEIGHT = { flash: 4, fireball: 3, ember: 1.5, shrapnel: 1, spark: 0.5 } as const;
/**
 * An effect's significance at spawn: its kind weight × its ANGULAR size from the viewer (size / distance, 1 once
 * the viewer is inside it). A capital's core flash 3 km away outranks a spark 40 km away by four orders.
 */
export function fxSignificance(weight: number, sizeM: number, distM: number): number {
  return weight * Math.min(1, Math.max(0, sizeM) / Math.max(1, distM));
}
/** A live effect's significance now: its spawn significance fading with its life (a dying ember is cheap to lose). */
export function fxLiveSignificance(sig0: number, t: number, life: number): number {
  return sig0 * Math.max(0, 1 - t / Math.max(1e-3, life));
}
/**
 * The admission rule for a FULL batch: the index of the live entry to EVICT for a spawn of significance `sig` —
 * the least significant one, and only if it is strictly less significant — or −1: drop the spawn.
 */
export function fxEvictIndex(n: number, sigAt: (i: number) => number, sig: number): number {
  let lo = -1, loSig = sig;
  for (let i = 0; i < n; i++) { const s = sigAt(i); if (s < loSig) { loSig = s; lo = i; } }
  return lo;
}

// ── R6: the war hulls' window / running-light layer (CPU mirror of lib/space/hull_look.ts) ───────────────
/** The shader's sin-fract hash (sign-safe: never `hash()` — three's toUint saturates negatives). */
export function hash2(a: number, b: number): number {
  const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
export const WINDOW = { rowM: 9, cellM: 6, band0: 0.38, band1: 0.62, gap0: 0.18, gap1: 0.82, occupancy: 0.42 } as const;
/**
 * The window mask at a hull-local point (metres) on a face whose dominant normal axis is `axis` (0 x, 1 y,
 * 2 z): rows every WINDOW.rowM along the face's "up", window cells every WINDOW.cellM along its "across",
 * a lit cell by hash vs occupancy, and only where the albedo is DARK (the panel lines of the cooked hulls)
 * and never on the stern face (plumes live there). 0 or 1 (the shader smooths the edges and filters at range).
 */
export function hullWindowMask(p: V3, axis: 0 | 1 | 2, seed: number, albedoLum: number): number {
  if (axis === 2 && p.z > 0) return 0; // the stern face (+Z) carries the engines
  const u = axis === 0 ? p.z : p.x;
  const v = axis === 1 ? p.z : p.y;
  const row = Math.floor(v / WINDOW.rowM), fv = v / WINDOW.rowM - row;
  const cell = Math.floor(u / WINDOW.cellM), fu = u / WINDOW.cellM - cell;
  if (fv < WINDOW.band0 || fv > WINDOW.band1) return 0;
  if (fu < WINDOW.gap0 || fu > WINDOW.gap1) return 0;
  if (hash2(row + seed * 17.0, cell + axis * 101.0) > WINDOW.occupancy) return 0;
  return albedoLum < 0.3 ? 1 : 0;
}
/** The dominant axis of a normal (the triplanar pick). */
export function dominantAxis(n: V3): 0 | 1 | 2 {
  const ax = Math.abs(n.x), ay = Math.abs(n.y), az = Math.abs(n.z);
  return ax >= ay && ax >= az ? 0 : ay >= az ? 1 : 2;
}
/** Window emission (HDR) — enough to bloom faintly up close and read as a lit texture at range. */
export const WINDOW_EMISSION = 2.4;
/** Nav points per master hull by length (a few hundred on a capital). */
export function navPointCount(lenM: number): number {
  return lenM >= 1000 ? 260 : lenM >= 90 ? 48 : lenM >= 25 ? 16 : 0;
}

// ── THE BODY LAYER LAW (2026-10-07): the depth CODES a body's shells are seated by (the TSL: space_light_look) ──────
/**
 * The sector camera is near 0.05 m / far 3,600 km: depth is 1 − near/z, so one code of a 24-bit buffer (and one float32
 * step of the vertex stage's clip z, which is what really limits it) spans z²·2⁻²⁴/near metres of view distance — 50 m
 * at 6.5 km, 1.2 km at 32 km, 11.9 km at the 100 km far-body anchor. A planet's shells are seated by CODES, so the
 * margin is always a few of whatever the buffer can resolve there.
 */
export type BodyLayer = 'behind' | 'over';
/** One code of a 24-bit depth buffer — also float32's step just under 1.0, where every far body's depth lives. */
export const DEPTH_CODE = 2 ** -24;
/**
 * How many codes a layer is seated from its true depth. The bed's sweep (verify/planet-shell-bed-probe.mjs, both
 * backends): two meshes disagree by up to ~2 codes — a 1-code margin still leaked 0.6–7.7 % of a disc, 2 codes four
 * limb pixels, 4 none. `behindFar` (an impostor: every far body is at one distance anyway, so the far plane costs
 * nothing) 16; `behindNear` (a real, near body: 4 keeps its limb haze in FRONT of the far planets seen through it) 4;
 * `over` 8.
 */
export const BODY_LAYER_CODES = { behindFar: 16, behindNear: 4, over: 8 } as const;
/**
 * An 'over' layer is pulled toward the camera only where it stands over ITS OWN body: at a vertex whose sight line
 * passes within this many planet radii of the centre. Elsewhere it stands with the 'behind' layers — on an impostor the
 * far plane, on a near body its true depth — so away from its own planet a far ring is ONE backdrop: it never jumps in
 * front of another world (a ringed giant's front half was drawn across a nearer planet) and never fights one (both are
 * impostors at one distance; the depth buffer has no order to give — the bed's `commons-far` view). The seat is
 * interpolated across a triangle, so the ring's reach is the disc plus half a radius and its mesh is cut fine against
 * that (`RING_MESH`): wherever the planet's own depth does not already separate the two by 4 codes, a triangle that
 * covers the disc has every vertex inside the reach (`verify:space-light` measures the worst pixel). The cloud shell
 * stands at 1.008 R — every vertex of it is inside its reach.
 */
export const OVER_REACH = { cloud: 1.06, ring: 1.5 } as const;
/**
 * THE FAR OPAQUE'S SEAT (space look, 2026-10-08). An opaque drawn at its TRUE distance lands ON the far plane once the
 * −near term of its clip z is lost in float32 (under an ulp of z from 2^19 m, under half of one from 2^20 m: the
 * black-hole bed's `far` rows through the sector's 0.05 m / 3,600 km planes — WebGL2 whole to 900 km, 66 % at 1,300,
 * 1 % at 3,000; WebGPU 62 % at 700 km, 1 % at 1,100), and THE BACKGROUND LAST then paints the sky over those pixels. A classic material has no vertex slot, so
 * the seat is a hardware depth bias of this many codes TOWARD the eye (never away: a bias may not cross the far
 * plane). Two codes stand it behind every far body's impostor (100 km is ~8 codes) and behind the black hole's cap (4).
 */
export const FAR_OPAQUE_PULL_CODES = 2;
/** The ring mesh (three's RingGeometry theta × phi segments). The picture is per FRAGMENT (radius, profile, the limb-plane
 *  cut), so the mesh only carries depth: 8 radial bands keep a triangle under 0.16 planet radii (3 bands were 0.41). */
export const RING_MESH = { theta: 384, radial: 8 } as const;
/** The view distance one depth code spans at distance `z` (m) on a `near` plane (far ≫ near). */
export function depthCodeMetres(z: number, near: number): number { return (z * z * DEPTH_CODE) / near; }
/** How many codes in front of the far plane a point at view distance `z` lies. */
export function depthCodesFromFar(z: number, near: number): number { return near / (z * DEPTH_CODE); }
/** The vertex test of an 'over' layer: does the sight line along `dir` (unit, from the camera) pass within `reach` of
 *  the body's centre (`toCentre` from the camera) — ahead of the camera, or with the camera itself inside the reach? */
export function overOwnBody(toCentre: V3, dir: V3, reach: number): boolean {
  const c2 = toCentre.x * toCentre.x + toCentre.y * toCentre.y + toCentre.z * toCentre.z;
  const tca = toCentre.x * dir.x + toCentre.y * dir.y + toCentre.z * dir.z;
  return c2 - tca * tca < reach * reach && (tca > 0 || c2 < reach * reach);
}
/**
 * The CPU mirror of a seated clip z. `far`: the body is an impostor (its world scale < 1); `overBody`: the vertex
 * stands over its own body (`overOwnBody`); `glClip`: WebGL's −w…w clip volume, where a depth code is two clip steps.
 * 'behind' never passes the far plane (z = w); 'over' never passes the near one — and away from its own body an 'over'
 * vertex is a backdrop like the rest of the body's shells (an impostor: the far plane; a near body: its true depth).
 */
export function layerClipZ(z: number, w: number, layer: BodyLayer, o: { far?: boolean; overBody?: boolean; glClip?: boolean; codes?: number } = {}): number {
  const unit = DEPTH_CODE * (o.glClip ? 2 : 1) * w;
  if (layer === 'behind') return Math.min(z + (o.codes ?? (o.far ? BODY_LAYER_CODES.behindFar : BODY_LAYER_CODES.behindNear)) * unit, w);
  if (o.overBody === false) return o.far ? Math.min(z + BODY_LAYER_CODES.behindFar * unit, w) : z;
  return Math.max(z - (o.codes ?? BODY_LAYER_CODES.over) * unit, o.glClip ? -w : 0);
}
